// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * What the PartBar and the inspector do with building parts, as plans: lists
 * of steps a component runs through `CaseActions` one after the other
 * (`runSteps`). Pure: no React, no DOM — a plan is data, so "which PATCHes,
 * in which order, with which mass split" is tested here.
 *
 *  - Flyt til type: `PATCH /survey/parts/{code} {type_id}` per part.
 *  - Opret ny type: one `POST /survey/types` copying name/eak/unit/treatment
 *    (and the build-up, `buildup_id`) and carrying the moved tonnes
 *    (`mass_t`), then the parts move, then each source type keeps what is
 *    left of its tonnes, split by stk count or quantity share (one PATCH per
 *    source). A server that computes the tonnes (ReUseX schema v37, contract
 *    gap 10 closed) recalculates both types from the parts that moved, so
 *    only a typed override (`mass_source: override`) is split. A server that
 *    ignores `mass_t` on create (before ruxd-6tz) gets it PATCHed.
 *  - Flet type: one `POST /survey/types/{id}/merge {into}` (`runMerge`); a
 *    server without the route (404/405) gets the old plan instead — every
 *    part moves, the tonnes are added, the samples are re-linked, the source
 *    is deleted (`mergePlan`).
 *  - Markér som fejl: `PATCH /survey/parts/{code} {dismissed: true}` per part
 *    (non-destructive; Gendan is `{dismissed: false}`), or — on a server that
 *    does not track `dismissed` — `DELETE /resources/{code}` (decision 7;
 *    scan parts are tombstoned so a sync does not re-create them). The caller
 *    puts the delete behind an armed confirm.
 *  - Omdøb: `PATCH /survey/types/{id} {name}`.
 *
 * Also the EditDialog helpers (`partChips`, `partCountText`, `primaryLabel`,
 * `primaryDisabled`, `titlePrefix`), moved here so they outlive the retiring
 * dialog — and the inspector's room grouping (`partsByRoom`, `roomShares`,
 * `topRoomShares`, `capWithFocus`) that keeps its chip lists short.
 */

import type {
  Sample,
  SurveyPart,
  SurveyPartPatch,
  SurveyType,
  SurveyTypeCreate,
  SurveyTypePatch,
} from '../api/types';
import { NO_ROOM_NAME } from './rooms';
import { partLabel } from '../kortlaegning/model';
import { approveBlocked } from '../kortlaegning/samples';
import { formatNumber, formatQuantity } from '../kortlaegning/vocab';
import { linksOf, type SampleLinks } from '../miljoe/model';

// ------------------------------------------------------- EditDialog helpers --

/** The head title's `RX-### · ` prefix when a part is selected, else ''. */
export function titlePrefix(part: SurveyPart | null): string {
  return part ? `${part.code} · ` : '';
}

export interface PartChip {
  /** `null` for the "Alle" chip. */
  code: string | null;
  label: string;
}

/** `Alle · {total} {unit}`, then `RX-### · room · {qty}` per part (see `partLabel`). */
export function partChips(type: SurveyType): PartChip[] {
  return [
    { code: null, label: `Alle · ${formatQuantity(type.quantity, type.unit)}` },
    ...type.parts.map((p) => ({
      code: p.code,
      label: `${partLabel(p)} · ${formatNumber(p.quantity)}`,
    })),
  ];
}

/** `n dele` (or `1 del`). */
export function partCountText(n: number): string {
  return `${n} ${n === 1 ? 'del' : 'dele'}`;
}

/** The primary review button: an already-approved type just moves on. */
export function primaryLabel(type: SurveyType): string {
  return type.review_status === 'approved' ? 'Godkendt ✓ — næste' : 'Godkend & næste ✓';
}

/**
 * Whether the primary review button is disabled. An already-approved type
 * only advances, so `afventer` does not block it.
 */
export function primaryDisabled(type: SurveyType, busy: boolean): boolean {
  return busy || (approveBlocked(type) && type.review_status !== 'approved');
}

// ------------------------------------------------------------------ steps --

export type PartStep =
  | { kind: 'patchPart'; code: string; patch: SurveyPartPatch }
  | { kind: 'patchType'; id: number; patch: SurveyTypePatch }
  | { kind: 'setSampleLinks'; sampleId: number; links: SampleLinks }
  | { kind: 'deleteResource'; code: string }
  | { kind: 'deleteType'; id: number };

/** The subset of `CaseActions` a plan runs through (each resolves null/false on failure). */
export interface StepActions {
  patchPart(code: string, patch: SurveyPartPatch): Promise<unknown>;
  patchType(id: number, patch: SurveyTypePatch): Promise<unknown>;
  setSampleLinks(id: number, links: SampleLinks): Promise<unknown>;
  deleteResource(code: string): Promise<boolean>;
  deleteType(id: number): Promise<unknown>;
}

/**
 * Runs `steps` in order, stopping at the first that fails (a null/false
 * answer: the store has already toasted why). Resolves how many succeeded and
 * whether all did.
 */
export async function runSteps(steps: readonly PartStep[], actions: StepActions): Promise<{ done: number; ok: boolean }> {
  let done = 0;
  for (const s of steps) {
    let result: unknown;
    switch (s.kind) {
      case 'patchPart':
        result = await actions.patchPart(s.code, s.patch);
        break;
      case 'patchType':
        result = await actions.patchType(s.id, s.patch);
        break;
      case 'setSampleLinks':
        result = await actions.setSampleLinks(s.sampleId, s.links);
        break;
      case 'deleteResource':
        result = await actions.deleteResource(s.code);
        break;
      case 'deleteType':
        result = await actions.deleteType(s.id);
        break;
    }
    if (result === null || result === false || result === undefined) return { done, ok: false };
    done += 1;
  }
  return { done, ok: true };
}

// ----------------------------------------------------------------- lookups --

/** The type holding part `code`, or null. */
export function ownerOf(types: readonly SurveyType[], code: string): SurveyType | null {
  return types.find((t) => t.parts.some((p) => p.code === code)) ?? null;
}

/** The part `code`, or null. */
export function partByCode(types: readonly SurveyType[], code: string): SurveyPart | null {
  for (const t of types) {
    const p = t.parts.find((x) => x.code === code);
    if (p) return p;
  }
  return null;
}

/** The picked codes that still exist, in pick order. */
export function livePicks(types: readonly SurveyType[], codes: readonly string[]): string[] {
  return codes.filter((c) => ownerOf(types, c) !== null);
}

export interface RoomShare {
  /** The room id; null for a part the scan put in no room (cannot be focused). */
  id: number | null;
  name: string;
  quantity: number;
  parts: number;
}

/** A type's parts in one room (`partsByRoom`). */
export interface RoomParts {
  /** Stable per room: `r{id}`, or `n{name}` for parts in no room. */
  key: string;
  /** The room id; null for parts the scan put in no room. */
  id: number | null;
  name: string;
  /** In the order they were given. */
  parts: SurveyPart[];
}

/** A type's parts folded by room, in room-id order (rooms without an id last, by name); each room's parts in the given order. */
export function partsByRoom(type: Pick<SurveyType, 'parts'>): RoomParts[] {
  const byKey = new Map<string, RoomParts>();
  for (const p of type.parts) {
    const name = p.room_name.trim() || (p.room_id !== null ? `Rum ${p.room_id}` : NO_ROOM_NAME);
    const key = p.room_id !== null ? `r${p.room_id}` : `n${name}`;
    const g = byKey.get(key) ?? { key, id: p.room_id, name, parts: [] };
    g.parts.push(p);
    byKey.set(key, g);
  }
  return [...byKey.values()].sort((a, b) => {
    if (a.id !== null && b.id !== null) return a.id - b.id;
    if (a.id !== null) return -1;
    if (b.id !== null) return 1;
    return a.name.localeCompare(b.name, 'da');
  });
}

/** "Fordeling på rum": a type's parts folded by room, in room-id order (rooms without an id last). */
export function roomShares(type: Pick<SurveyType, 'parts'>): RoomShare[] {
  return partsByRoom(type).map((g) => ({
    id: g.id,
    name: g.name,
    quantity: g.parts.reduce((s, p) => s + p.quantity, 0),
    parts: g.parts.length,
  }));
}

/** Above this many parts the inspector's "Dele" lists rooms, not parts (`PartChips`). */
export const PART_GROUP_MIN = 12;

/** How many part chips an open room shows before "Vis alle" (`PartChips`). */
export const ROOM_PART_LIMIT = 30;

/** How many rooms "Fordeling på rum" shows before "+ n flere" (`RoomChips`). */
export const ROOM_SHARE_LIMIT = 6;

/**
 * The first `limit` items, and the rest; the focused item (if any) is moved
 * from the rest to the end of the shown ones so it never hides. A list at
 * most one longer than `limit` is shown whole — "+ 1 flere" saves nothing.
 */
export function capWithFocus<T>(
  items: readonly T[],
  limit: number,
  isFocused: (item: T) => boolean,
): { shown: T[]; hidden: T[] } {
  if (items.length <= limit + 1) return { shown: [...items], hidden: [] };
  const shown = items.slice(0, limit);
  const hidden = items.slice(limit);
  const i = hidden.findIndex(isFocused);
  if (i >= 0) shown.push(...hidden.splice(i, 1));
  return { shown, hidden };
}

/**
 * "Fordeling på rum" when a type is spread over many rooms: the `limit`
 * largest by quantity (ties keep room order), plus the focused room when it
 * ranks lower; the rest hidden, largest first. Up to `limit + 1` rooms come
 * back whole and in the order given.
 */
export function topRoomShares(
  shares: readonly RoomShare[],
  limit: number,
  focusedId: number | null,
): { shown: RoomShare[]; hidden: RoomShare[] } {
  if (shares.length <= limit + 1) return { shown: [...shares], hidden: [] };
  const sorted = [...shares].sort((a, b) => b.quantity - a.quantity);
  return capWithFocus(sorted, limit, (r) => r.id !== null && r.id === focusedId);
}

/**
 * The part the evidence buttons act on: the focused part when it is the
 * type's, else the first picked part of the type, else its first part from
 * the scan, else its first part.
 */
export function activePart(type: Pick<SurveyType, 'parts'>, partCode: string | null, picked: readonly string[]): string | null {
  const codes = new Set(type.parts.map((p) => p.code));
  if (partCode !== null && codes.has(partCode)) return partCode;
  const pick = picked.find((c) => codes.has(c));
  if (pick) return pick;
  const sorted = [...type.parts].sort((a, b) => a.code.localeCompare(b.code));
  return (sorted.find((p) => p.instance_guid !== null) ?? sorted[0])?.code ?? null;
}

/** "3 dele valgt" / "1 del valgt". */
export function partBarLabel(n: number): string {
  return `${partCountText(n)} valgt`;
}

/**
 * The types "Flyt til type" offers: every non-rejected type except the one
 * that already holds all the picked parts.
 */
export function moveTargets(types: readonly SurveyType[], codes: readonly string[]): SurveyType[] {
  const owners = new Set(codes.map((c) => ownerOf(types, c)?.id).filter((id): id is number => id !== undefined));
  const sole = owners.size === 1 ? [...owners][0] : null;
  return types.filter((t) => t.review_status !== 'rejected' && t.id !== sole);
}

// ------------------------------------------------------------------ plans --

/** Flyt til type: each picked part not already in the target moves there. */
export function movePlan(types: readonly SurveyType[], codes: readonly string[], targetId: number): PartStep[] {
  return livePicks(types, codes)
    .filter((c) => ownerOf(types, c)?.id !== targetId)
    .map((code) => ({ kind: 'patchPart' as const, code, patch: { type_id: targetId } }));
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export interface NewTypePlan {
  /** `POST /survey/types`, copying the first picked part's type. */
  create: SurveyTypeCreate;
  /** The steps once the new type exists. */
  then: (created: Pick<SurveyType, 'id' | 'mass_t'>) => PartStep[];
  /** The type the name/fields were copied from. */
  sourceId: number;
}

/** The new type's name: the source's, marked as new. */
export function newTypeName(source: Pick<SurveyType, 'name'>): string {
  return `${source.name} · ny type`;
}

/**
 * The share of a source type's tonnes that leaves with `moving` of its parts:
 * by count for stk, by quantity otherwise (an even count share when the
 * type's quantity is 0).
 */
export function massShare(source: SurveyType, moving: readonly SurveyPart[]): number {
  if (source.mass_t === null || source.parts.length === 0 || moving.length === 0) return 0;
  if (source.unit === 'stk' || source.quantity <= 0) return round2((source.mass_t * moving.length) / source.parts.length);
  const q = moving.reduce((s, p) => s + p.quantity, 0);
  return round2(source.mass_t * Math.min(1, q / source.quantity));
}

/**
 * Whether moving parts must carry the type's tonnes by hand: always on an
 * older server; on one that computes them (`mass_source` sent) only a typed
 * override — calculated tonnes follow the parts by themselves.
 */
export function splitsMass(type: Pick<SurveyType, 'mass_source'>): boolean {
  return type.mass_source === undefined || type.mass_source === 'override';
}

/**
 * Opret ny type from the picked parts (null when none of them exists any
 * more). The create carries the moved tonnes; `then(created)` PATCHes them
 * only when the server did not take them (a server before ruxd-6tz ignores
 * `mass_t` on create).
 */
export function newTypePlan(types: readonly SurveyType[], codes: readonly string[]): NewTypePlan | null {
  const live = livePicks(types, codes);
  if (live.length === 0) return null;
  const source = ownerOf(types, live[0]) as SurveyType;
  const create: SurveyTypeCreate = {
    name: newTypeName(source),
    eak_code: source.eak_code,
    unit: source.unit,
    treatment: source.treatment,
  };
  if (source.bim7aa_code) create.bim7aa_code = source.bim7aa_code;
  if (source.buildup_id) create.buildup_id = source.buildup_id;

  // Group the picks by the type they leave, for the tonnes split.
  const bySource = new Map<number, { type: SurveyType; parts: SurveyPart[] }>();
  for (const code of live) {
    const t = ownerOf(types, code) as SurveyType;
    const entry = bySource.get(t.id) ?? { type: t, parts: [] };
    entry.parts.push(t.parts.find((p) => p.code === code) as SurveyPart);
    bySource.set(t.id, entry);
  }

  // The tonnes leaving each source with its parts (gap 10: split client-side).
  const shares: { id: number; left: number }[] = [];
  let moved = 0;
  for (const { type, parts } of bySource.values()) {
    if (type.mass_t === null || !splitsMass(type)) continue;
    const share = massShare(type, parts);
    moved += share;
    shares.push({ id: type.id, left: round2(type.mass_t - share) });
  }
  const mass = shares.length > 0 ? round2(moved) : null;
  if (mass !== null) create.mass_t = mass;

  return {
    create,
    sourceId: source.id,
    then: (created) => {
      const steps: PartStep[] = live.map((code) => ({ kind: 'patchPart', code, patch: { type_id: created.id } }));
      for (const { id, left } of shares) steps.push({ kind: 'patchType', id, patch: { mass_t: left } });
      if (mass !== null && created.mass_t !== mass) steps.push({ kind: 'patchType', id: created.id, patch: { mass_t: mass } });
      return steps;
    },
  };
}

/** The samples linked to a type (either side of the link may name it). */
export function samplesOfType(type: Pick<SurveyType, 'id' | 'sample_ids'>, samples: readonly Sample[]): Sample[] {
  return samples.filter((s) => s.type_ids.includes(type.id) || type.sample_ids.includes(s.id));
}

/**
 * Flet type: every part of `source` moves to `target`, the tonnes are added
 * (when either has any), each sample linked to the whole source is linked to
 * the target instead — its single-part links kept as they are — and the
 * emptied source is deleted. A sample that covers only some of the source's
 * parts needs no new link: the parts keep their codes, so the part links
 * follow them to the target (linking the whole target would widen it).
 */
export function mergePlan(source: SurveyType, target: SurveyType, samples: readonly Sample[]): PartStep[] {
  const steps: PartStep[] = [...source.parts]
    .sort((a, b) => a.code.localeCompare(b.code))
    .map((p) => ({ kind: 'patchPart', code: p.code, patch: { type_id: target.id } }));
  if (source.mass_t !== null && splitsMass(source)) {
    steps.push({ kind: 'patchType', id: target.id, patch: { mass_t: round2((target.mass_t ?? 0) + source.mass_t) } });
  }
  const sourceCodes = new Set(source.parts.map((p) => p.code));
  for (const s of samplesOfType(source, samples)) {
    const links = linksOf(s);
    const partOnly = !links.type_ids.includes(source.id) && links.part_codes.some((c) => sourceCodes.has(c));
    if (partOnly) continue;
    const ids = [...new Set([...links.type_ids.filter((id) => id !== source.id), target.id])];
    steps.push({ kind: 'setSampleLinks', sampleId: s.id, links: { type_ids: ids, part_codes: [...links.part_codes] } });
  }
  steps.push({ kind: 'deleteType', id: source.id });
  return steps;
}

/**
 * The outcome of a single-call write that may be missing on an older
 * server: the server's answer, `'unsupported'` (the route is not there —
 * nothing was written, nothing toasted), or null (it failed and toasted).
 */
export type Maybe<T> = T | 'unsupported' | null;

/** The merge action `runMerge` tries first (`CaseActions.mergeType`). */
export interface MergeActions extends StepActions {
  mergeType(id: number, into: number): Promise<Maybe<unknown>>;
}

/**
 * Flet type: one `POST /survey/types/{id}/merge`; on a server without the
 * route, the step-by-step `mergePlan` (gap 9's interim, non-atomic).
 */
export async function runMerge(
  source: SurveyType,
  target: SurveyType,
  samples: readonly Sample[],
  actions: MergeActions,
): Promise<{ ok: boolean; fallback: boolean }> {
  const r = await actions.mergeType(source.id, target.id);
  if (r !== 'unsupported') return { ok: r !== null, fallback: false };
  const { ok } = await runSteps(mergePlan(source, target, samples), actions);
  return { ok, fallback: true };
}

/**
 * Markér som fejl: `PATCH {dismissed: true}` per picked part where the server
 * tracks `dismissed` (`tracksDismissed`), else delete each (decision 7).
 */
export function dismissPlan(types: readonly SurveyType[], codes: readonly string[], serverDismisses = false): PartStep[] {
  return livePicks(types, codes).map((code) =>
    serverDismisses ? { kind: 'patchPart', code, patch: { dismissed: true } } : { kind: 'deleteResource', code },
  );
}

/** Gendan: un-dismiss each part (only offered where the server tracks `dismissed`). */
export function restorePlan(codes: readonly string[]): PartStep[] {
  return codes.map((code) => ({ kind: 'patchPart', code, patch: { dismissed: false } }));
}

/** Omdøb: the patch, or null when the name is blank or unchanged. */
export function renamePatch(type: Pick<SurveyType, 'name'>, draft: string): SurveyTypePatch | null {
  const name = draft.trim();
  if (!name || name === type.name) return null;
  return { name };
}

// ------------------------------------------------------------------ copy --

/** The armed "Markér som fejl" button's text; `deletes` when the fallback deletes the parts. */
export function dismissLabel(n: number, armed: boolean, deletes = true): string {
  if (!armed) return 'Markér som fejl';
  return deletes ? `Bekræft: slet ${partCountText(n)}` : `Bekræft: markér ${partCountText(n)}`;
}

/** The armed "Slet type" button's text. */
export function deleteTypeLabel(type: Pick<SurveyType, 'parts'>, armed: boolean): string {
  if (!armed) return 'Slet type';
  const n = type.parts.length;
  return n === 0 ? 'Bekræft: slet typen' : `Bekræft: slet typen og ${partCountText(n)}`;
}

/** The toast after a PartBar plan, by what it did. */
export function planToast(
  kind: 'move' | 'new' | 'dismiss' | 'dismissDelete' | 'restore' | 'merge',
  n: number,
  name = '',
): string {
  const dele = partCountText(n);
  switch (kind) {
    case 'move':
      return `${dele} flyttet til »${name}«`;
    case 'new':
      return `»${name}« oprettet med ${dele}`;
    case 'dismiss':
      return `${dele} markeret som fejldetektion — kan gendannes i Tabel › Dele`;
    case 'dismissDelete':
      return `${dele} markeret som fejldetektion og slettet`;
    case 'restore':
      return `${dele} gendannet`;
    case 'merge':
      return `Flettet ind i »${name}« (${dele})`;
  }
}
